import { cloneElement, createContext, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Icon } from '@quintoandar-tokko/jopijs';
import ToolTip from '@/components/ToolTip/ToolTip';
import Trans from '@/components/shared/Trans';
import { setSelectedIds } from '@/store/slices/components/filterBar/FilterBarSlice';
import { joinSelectionNames } from './utils';
import translations from './FilterBar.trans.json';
import styles from './filterBar.module.scss';

const FilterSelectionContext = createContext(false);

export function useFilterSelectionEnabled() {
  return useContext(FilterSelectionContext);
}

export function FilterSelectionProvider({ enabled = false, children }) {
  return (
    <FilterSelectionContext.Provider value={enabled}>
      {children}
    </FilterSelectionContext.Provider>
  );
}

export function useFilterSelection(
  type,
  { allLabel, allSelected = false, clearable = true } = {}
) {
  const dispatch = useDispatch();
  const selectedIds = useSelector(
    (state) => state.FilterBarSlice.filters.selectedIds
  );
  const items = (selectedIds || []).filter((item) => item.filter_type === type);
  const orWord = Trans(translations, 'FilterOr');

  return {
    items,
    filled: items.length > 0,
    tooltip:
      allSelected && allLabel ? allLabel : joinSelectionNames(items, orWord),
    onClear: clearable
      ? () => dispatch(setSelectedIds({ idList: [], type }))
      : undefined,
    clearLabel: Trans(translations, 'ClearSelection'),
  };
}

export default function FilterSelectControl({
  filled = false,
  pending = false,
  tooltip = '',
  onClear,
  clearLabel,
  children,
}) {
  const enabled = useContext(FilterSelectionContext);
  if (!enabled) {
    return cloneElement(children, {
      filled,
      style: {
        ...(children.props.style || {}),
        borderColor: pending
          ? 'var(--colors-text-icon-neutral-onground)'
          : 'var(--colors-border-neutral-onground)',
      },
    });
  }

  const trigger =
    filled && tooltip ? (
      <ToolTip
        title={tooltip}
        className={styles.tipTrigger}
        tipClassName={styles.tip}
        data-testid="filter-select-trigger"
      >
        {children}
      </ToolTip>
    ) : (
      children
    );

  return (
    <Box
      className={`${styles.filterControl} ${
        filled ? styles.filterControlFilled : ''
      }`}
    >
      {trigger}
      {filled && onClear && (
        <>
          <Box className={styles.filterDivider} aria-hidden="true" />
          <Box
            as="button"
            type="button"
            className={styles.filterClear}
            aria-label={clearLabel}
            onClick={(event) => {
              event.stopPropagation();
              onClear();
            }}
          >
            <Icon icon="icon-cerrar" fontSize="14px" />
          </Box>
        </>
      )}
    </Box>
  );
}
